import { memo } from "react";
import { View } from "@src/components";
import { DraggableDayCell } from "../DraggableDayCell";
import { type GridStore } from "./grid-store";
import { spacing } from "@src/unistyles";
import { CELL_MARGIN_BOTTOM } from "./constants";

// 96 = getSlotsPerDay(15) — the finest supported granularity.
// All 96 cells are always mounted; the parent grid component clips
// overflow via a calculated row height so granularity switches never
// cause DayColumn or its cells to re-render.
const MAX_SLOTS = 96;

// Cells stack vertically — disable flex grow and add vertical spacing
const CELL_STYLE = { flex: 0, marginBottom: CELL_MARGIN_BOTTOM } as const;

// ── SlotCell — thin wrapper for stable props ─────────────────────
// Custom comparator ensures DraggableDayCell is never re-rendered by
// parent changes — all reactivity comes from store subscriptions
// inside DraggableDayCell itself (value, drag state).

interface SlotCellProps {
  day: number | string;
  hour: number;
  onPress: (day: number | string, hour: number) => void;
  registerCell: (day: number | string, hour: number, ref: any) => void;
  store: GridStore;
}

const SlotCell = memo<SlotCellProps>(
  ({ day, hour, onPress, registerCell, store }) => (
    <DraggableDayCell
      day={day}
      hour={hour}
      onPress={onPress}
      registerCell={registerCell}
      store={store}
      style={CELL_STYLE}
    />
  ),
  (prev, next) => prev.day === next.day && prev.hour === next.hour,
);

// ── DayColumn ─────────────────────────────────────────────────────
// Renders MAX_SLOTS SlotCells. DayColumn has no store subscriptions
// and all props are stable, so its memo holds during granularity
// switches and drag operations — only individual SlotCells with
// changed drag/active state re-render.

interface DayColumnProps {
  day: number | string;
  onPress: (day: number | string, hour: number) => void;
  registerCell: (day: number | string, hour: number, ref: any) => void;
  store: GridStore;
}

export const DayColumn = memo<DayColumnProps>(({ day, onPress, registerCell, store }) => (
  <View flex={1}>
    {Array.from({ length: MAX_SLOTS }, (_, slot) => (
      <SlotCell key={slot} day={day} hour={slot} onPress={onPress} registerCell={registerCell} store={store} />
    ))}
  </View>
));
